import React from 'react'
import { Button, CircularProgress, Tooltip } from '@mui/material'
import type { SxProps, Theme } from '@mui/material/styles'
import usePrepListStore, { type PrepListState } from '../../store/prepListStore'

type EnterMyOnlySelection = Parameters<PrepListState['enterMyOnly']>[0]

type MyPrepToggleButtonProps = {
  disabled?: boolean
  sx?: SxProps<Theme>
  className?: string
  onToggled?: (enabled: boolean) => void
  onToggleStart?: (nextEnabled: boolean) => void
  onToggleError?: (error: unknown, nextEnabled: boolean) => void
  getEnterSelection?: () => EnterMyOnlySelection
}

const defaultPillSx: SxProps<Theme> = {
  borderRadius: 999,
  fontWeight: 600,
  textTransform: 'none',
  px: 1.75,
  py: 0.25,
  minHeight: 32,
  whiteSpace: 'nowrap',
}

const MyPrepToggleButton: React.FC<MyPrepToggleButtonProps> = ({
  disabled = false,
  sx,
  className,
  onToggled,
  onToggleStart,
  onToggleError,
  getEnterSelection,
}) => {
  const myOnly = usePrepListStore((state) => state.myOnly)
  const myIdsLoading = usePrepListStore((state) => state.myIdsLoading)
  const myCount = usePrepListStore((state) => state.myCount)
  const enterMyOnly = usePrepListStore((state) => state.enterMyOnly)
  const exitMyOnly = usePrepListStore((state) => state.exitMyOnly)

  const loading = myIdsLoading
  const tooltip = myOnly ? 'Return to all items' : "Show only items you've saved"
  const buttonLabel = `My Prep • ${myCount}`
  const computedDisabled = disabled || loading

  const handleClick = React.useCallback(async () => {
    if (computedDisabled) {
      return
    }

    const nextEnabled = !myOnly
    onToggleStart?.(nextEnabled)

    try {
      if (!myOnly) {
        const selection = getEnterSelection?.()
        await enterMyOnly(selection)
      } else {
        await exitMyOnly()
      }
      onToggled?.(nextEnabled)
    } catch (error) {
      onToggleError?.(error, nextEnabled)
    }
  }, [
    computedDisabled,
    enterMyOnly,
    exitMyOnly,
    getEnterSelection,
    myOnly,
    onToggleError,
    onToggleStart,
    onToggled,
  ])

  return (
    <Tooltip title={tooltip} enterDelay={250} describeChild>
      <span>
        <Button
          type="button"
          size="small"
          color="primary"
          variant={myOnly ? 'contained' : 'outlined'}
          onClick={handleClick}
          disabled={computedDisabled}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}
          aria-pressed={myOnly}
          aria-busy={loading ? 'true' : undefined}
          className={className}
          sx={[defaultPillSx, ...(Array.isArray(sx) ? sx : sx ? [sx] : [])]}
        >
          {buttonLabel}
        </Button>
      </span>
    </Tooltip>
  )
}

export default MyPrepToggleButton
